// frontend/src/components/utilities/InputImageBox.js
import React, { useEffect, useRef } from 'react';

export default function InputImageBox({
  inputImage,
  uiDisabled = false,
  credits = 0,
  onInputClick,
  autoOpenSignal = null,
  isProcessing = false,
  boxStyle,
  imgStyle,
  invalidateProcessing,
  controlsConfig,
  values,
  setters,
  capitanApi,
  onCropConfirm, // optional
}) {
  const cfg = controlsConfig || {};
  const v = values || {};
  const s = setters || {};

  const [isOpen, setIsOpen] = React.useState(false);
  const [localImage, setLocalImage] = React.useState(inputImage || null);

  const [isCropMode, setIsCropMode] = React.useState(false);
  const [cropRect, setCropRect] = React.useState(null);
  const [displayedImageRect, setDisplayedImageRect] = React.useState(null);
  const [naturalSize, setNaturalSize] = React.useState({ width: 0, height: 0 });
  const [isSubmittingCrop, setIsSubmittingCrop] = React.useState(false);

  const imgRef = useRef(null);
  const boxRef = useRef(null);

  const dragRef = useRef({
    active: false,
    mode: null,
    pointerId: null,
    startClientX: 0,
    startClientY: 0,
    startRect: null,
  });

  const didAutoOpenRef = useRef(false);

  useEffect(() => {
    setLocalImage(inputImage || null);
  }, [inputImage]);

  useEffect(() => {
    if (!autoOpenSignal) return;
    if (didAutoOpenRef.current) return;
    didAutoOpenRef.current = true;
    setIsOpen(true);
  }, [autoOpenSignal]);

  useEffect(() => {
    if (!localImage) {
      didAutoOpenRef.current = false;
      setIsOpen(false);
      setIsCropMode(false);
      setCropRect(null);
      setDisplayedImageRect(null);
      setNaturalSize({ width: 0, height: 0 });
      setIsSubmittingCrop(false);
    }
  }, [localImage]);

  const _boxStyle =
    typeof boxStyle === 'function'
      ? boxStyle()
      : boxStyle || {
          width: '100%',
          height: '100%',
          border: '2px dashed #94a3b8',
          borderRadius: '10px',
          backgroundColor: '#f8fafc',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
        };

  const _imgStyle =
    imgStyle || {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    };

  const clickable = credits > 0 && !uiDisabled;

  const clampInt = (n, lo, hi, fb) => {
    const x = parseInt(String(n ?? ''), 10);
    if (!Number.isFinite(x)) return fb;
    return Math.max(lo, Math.min(hi, x));
  };

  const setAndInvalidate = (setter, nextVal) => {
    if (typeof setter === 'function') setter(nextVal);
    if (typeof invalidateProcessing === 'function') invalidateProcessing();
  };

  const Row = ({ label, children }) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        marginBottom: '8px',
        gap: '1vh',
        opacity: uiDisabled ? 0.6 : 1,
        pointerEvents: uiDisabled ? 'none' : 'auto',
      }}
    >
      <span
        style={{
          fontSize: '2.5vh',
          fontWeight: 600,
          color: '#e5e7eb',
          letterSpacing: '0.5px',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );

  const Stepper = ({
    value,
    setValue,
    min,
    max,
    title,
    widthVh = 3,
    onAfterChange,
  }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1vh' }}>
      <button
        type="button"
        disabled={uiDisabled}
        onClick={
          uiDisabled
            ? undefined
            : () => {
                const next = Math.max(min, (value ?? min) - 1);
                setAndInvalidate(setValue, next);
                onAfterChange?.(next);
              }
        }
        style={{
          width: `${widthVh}vh`,
          height: `${widthVh}vh`,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.18)',
          border: '1px solid rgba(255,255,255,0.3)',
          color: 'white',
          fontSize: '2vh',
          lineHeight: 0,
          textAlign: 'center',
          cursor: uiDisabled ? 'not-allowed' : 'pointer',
          opacity: uiDisabled ? 0.6 : 1,
        }}
      >
        –
      </button>

      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        disabled={uiDisabled}
        value={String(value ?? min)}
        onChange={
          uiDisabled
            ? undefined
            : (e) => {
                const raw = e.target.value.replace(/\D/g, '');
                const next = clampInt(raw === '' ? min : raw, min, max, min);
                setAndInvalidate(setValue, next);
                onAfterChange?.(next);
              }
        }
        style={{
          width: `${widthVh}vh`,
          fontSize: '2vh',
          padding: '0.4vh 0.6vh',
          background: 'rgba(255,255,255,0.12)',
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: '0.6vh',
          color: 'white',
          textAlign: 'center',
          outline: 'none',
          backdropFilter: 'blur(4px)',
          margin: 0,
          opacity: uiDisabled ? 0.6 : 1,
          cursor: uiDisabled ? 'not-allowed' : 'text',
        }}
        title={title}
      />

      <button
        type="button"
        disabled={uiDisabled}
        onClick={
          uiDisabled
            ? undefined
            : () => {
                const next = Math.min(max, (value ?? min) + 1);
                setAndInvalidate(setValue, next);
                onAfterChange?.(next);
              }
        }
        style={{
          width: `${widthVh}vh`,
          height: `${widthVh}vh`,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.18)',
          border: '1px solid rgba(255,255,255,0.3)',
          color: 'white',
          fontSize: '2vh',
          lineHeight: 0,
          textAlign: 'center',
          cursor: uiDisabled ? 'not-allowed' : 'pointer',
          opacity: uiDisabled ? 0.6 : 1,
        }}
      >
        +
      </button>
    </div>
  );

  const Flag = ({ label, value, setValue }) => (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: uiDisabled ? 'not-allowed' : 'pointer',
        gap: '1vh',
      }}
    >
      <span>{label}</span>
      <span
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={uiDisabled}
          onChange={
            uiDisabled
              ? undefined
              : (e) => setAndInvalidate(setValue, e.target.checked)
          }
          style={{
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            width: '2.4vh',
            height: '2.4vh',
            borderRadius: '0.5vh',
            border: '0.25vh solid rgba(255,255,255,0.8)',
            backgroundColor: 'rgba(15,23,42,0.6)',
            display: 'inline-block',
            cursor: uiDisabled ? 'not-allowed' : 'pointer',
          }}
        />
        {Boolean(value) && (
          <span
            style={{
              position: 'absolute',
              fontSize: '1.8vh',
              color: 'white',
              pointerEvents: 'none',
            }}
          >
            ✓
          </span>
        )}
      </span>
    </label>
  );

  const renderNumColors = () => {
    if (!cfg.numColors?.enabled) return null;
    const min = cfg.numColors?.min ?? 2;
    const max = cfg.numColors?.max ?? 8;
    return (
      <Row label="Number of Colors:">
        <Stepper
          value={v.pendingNumColors}
          setValue={s.setPendingNumColors}
          min={min}
          max={max}
          title="Number of colors"
        />
      </Row>
    );
  };

  const renderNumPicks = () => {
    if (!cfg.numPicks?.enabled) return null;
    const min = cfg.numPicks?.min ?? 1;
    const max = cfg.numPicks?.max ?? 4;
    return (
      <Row label="Number of Picks:">
        <Stepper
          value={v.numPicks}
          setValue={s.setNumPicks}
          min={min}
          max={max}
          title="Number of picks"
        />
      </Row>
    );
  };

  const renderShape = () => {
    if (!cfg.shape?.enabled) return null;

    const modeVal = cfg.shape?.mode ?? v.shapeMode ?? cfg.shape?.default ?? 'circle';
    const setModeFn = cfg.shape?.setMode ?? s.setShapeMode;

    const sidesVal =
      cfg.shape?.iterativeSides ??
      v.iterativeSides ??
      cfg.shape?.iterativeMin ??
      cfg.shape?.minSides ??
      6;

    const setSidesFn = cfg.shape?.setIterativeSides ?? s.setIterativeSides;

    const shapeValue = String(modeVal);
    const showIterative = shapeValue === 'iterative';

    return (
      <Row label="Shape:">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1vh',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
          }}
        >
          <select
            value={shapeValue}
            disabled={uiDisabled}
            onChange={
              uiDisabled
                ? undefined
                : (e) => setAndInvalidate(setModeFn, e.target.value)
            }
            style={{
              padding: '0.4vh 0.8vh',
              fontSize: '2vh',
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '0.6vh',
              color: '#e5e7eb',
              backdropFilter: 'blur(4px)',
              minWidth: '10vh',
              opacity: uiDisabled ? 0.6 : 1,
              cursor: uiDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {(cfg.shape?.options || [
              'iterative',
              'circle',
              'square',
              'pentagon',
              'hexagon',
              'octagon',
            ]).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>

          {showIterative && (
            <Stepper
              value={sidesVal}
              setValue={setSidesFn}
              min={cfg.shape?.minSides ?? cfg.shape?.iterativeMin ?? 6}
              max={cfg.shape?.maxSides ?? cfg.shape?.iterativeMax ?? 10}
              title="Number of sides for iterative shape"
            />
          )}
        </div>
      </Row>
    );
  };

  const renderFlags = () => {
    const flags = cfg.flags || {};
    const anyEnabled = Object.values(flags).some((x) => x?.enabled);
    if (!anyEnabled) return null;

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.4vh',
          fontSize: '2vh',
          opacity: uiDisabled ? 0.6 : 1,
          pointerEvents: uiDisabled ? 'none' : 'auto',
        }}
      >
        {flags.removeNoisyBackground?.enabled && (
          <Flag
            label={flags.removeNoisyBackground?.label || 'Remove Noisy Background'}
            value={v.removeNoisyBackground}
            setValue={s.setRemoveNoisyBackground}
          />
        )}

        {flags.closeBackgroundHoles?.enabled && (
          <Flag
            label={flags.closeBackgroundHoles?.label || 'Close Background Holes'}
            value={v.closeBackgroundHoles}
            setValue={s.setCloseBackgroundHoles}
          />
        )}

        {flags.removeSmallIslands?.enabled && (
          <Flag
            label={flags.removeSmallIslands?.label || 'Remove Small Islands'}
            value={v.removeSmallIslands}
            setValue={s.setRemoveSmallIslands}
          />
        )}

        {flags.speckleAutoCleanup?.enabled && (
          <Flag
            label={flags.speckleAutoCleanup?.label || 'Speckle Auto-Cleanup'}
            value={v.speckleAutoCleanup}
            setValue={s.setSpeckleAutoCleanup}
          />
        )}

        {flags.makeFreestanding?.enabled && (
          <Flag
            label={flags.makeFreestanding?.label || 'Make Freestanding'}
            value={v.makeFreestanding}
            setValue={s.setMakeFreestanding}
          />
        )}
      </div>
    );
  };

  const calculateDisplayedImageRect = React.useCallback(() => {
    const img = imgRef.current;
    const box = boxRef.current;
    if (!img || !box) return;

    const boxRect = box.getBoundingClientRect();
    const containerWidth = boxRect.width;
    const containerHeight = boxRect.height;

    const naturalWidth = img.naturalWidth || 0;
    const naturalHeight = img.naturalHeight || 0;

    if (!naturalWidth || !naturalHeight || !containerWidth || !containerHeight) return;

    const imageAspect = naturalWidth / naturalHeight;
    const boxAspect = containerWidth / containerHeight;

    let displayedWidth;
    let displayedHeight;
    let offsetX;
    let offsetY;

    if (imageAspect > boxAspect) {
      displayedWidth = containerWidth;
      displayedHeight = containerWidth / imageAspect;
      offsetX = 0;
      offsetY = (containerHeight - displayedHeight) / 2;
    } else {
      displayedHeight = containerHeight;
      displayedWidth = containerHeight * imageAspect;
      offsetY = 0;
      offsetX = (containerWidth - displayedWidth) / 2;
    }

    const rect = {
      left: offsetX,
      top: offsetY,
      width: displayedWidth,
      height: displayedHeight,
    };

    setDisplayedImageRect(rect);
    setNaturalSize({ width: naturalWidth, height: naturalHeight });

    setCropRect((prev) => {
      const minSize = 40;

      if (prev) {
        const x = Math.max(rect.left, Math.min(prev.x, rect.left + rect.width - minSize));
        const y = Math.max(rect.top, Math.min(prev.y, rect.top + rect.height - minSize));
        const width = Math.max(minSize, Math.min(prev.width, rect.left + rect.width - x));
        const height = Math.max(minSize, Math.min(prev.height, rect.top + rect.height - y));
        return { x, y, width, height };
      }

      return {
        x: rect.left + rect.width * 0.1,
        y: rect.top + rect.height * 0.1,
        width: rect.width * 0.8,
        height: rect.height * 0.8,
      };
    });
  }, []);

  useEffect(() => {
    const handleResize = () => calculateDisplayedImageRect();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateDisplayedImageRect]);

  const beginPointerDrag = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();

    if (!cropRect || !displayedImageRect) return;

    dragRef.current = {
      active: true,
      mode,
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startRect: { ...cropRect },
    };

    try {
      e.currentTarget.setPointerCapture?.(e.pointerId);
    } catch (_) {}
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    const bounds = displayedImageRect;

    if (!drag.active || drag.pointerId !== e.pointerId || !drag.startRect || !bounds) return;

    e.preventDefault();

    const dx = e.clientX - drag.startClientX;
    const dy = e.clientY - drag.startClientY;
    const minSize = 40;

    let next = { ...drag.startRect };

    if (drag.mode === 'move') {
      next.x = drag.startRect.x + dx;
      next.y = drag.startRect.y + dy;

      next.x = Math.max(bounds.left, Math.min(next.x, bounds.left + bounds.width - next.width));
      next.y = Math.max(bounds.top, Math.min(next.y, bounds.top + bounds.height - next.height));
    }

    if (drag.mode === 'resize-nw') {
      const right = drag.startRect.x + drag.startRect.width;
      const bottom = drag.startRect.y + drag.startRect.height;

      next.x = Math.max(bounds.left, Math.min(drag.startRect.x + dx, right - minSize));
      next.y = Math.max(bounds.top, Math.min(drag.startRect.y + dy, bottom - minSize));
      next.width = right - next.x;
      next.height = bottom - next.y;
    }

    if (drag.mode === 'resize-ne') {
      const left = drag.startRect.x;
      const bottom = drag.startRect.y + drag.startRect.height;

      const right = Math.max(
        left + minSize,
        Math.min(drag.startRect.x + drag.startRect.width + dx, bounds.left + bounds.width)
      );

      next.x = left;
      next.y = Math.max(bounds.top, Math.min(drag.startRect.y + dy, bottom - minSize));
      next.width = right - left;
      next.height = bottom - next.y;
    }

    if (drag.mode === 'resize-sw') {
      const right = drag.startRect.x + drag.startRect.width;
      const top = drag.startRect.y;

      next.x = Math.max(bounds.left, Math.min(drag.startRect.x + dx, right - minSize));

      const bottom = Math.max(
        top + minSize,
        Math.min(drag.startRect.y + drag.startRect.height + dy, bounds.top + bounds.height)
      );

      next.y = top;
      next.width = right - next.x;
      next.height = bottom - top;
    }

    if (drag.mode === 'resize-se') {
      const left = drag.startRect.x;
      const top = drag.startRect.y;

      const right = Math.max(
        left + minSize,
        Math.min(drag.startRect.x + drag.startRect.width + dx, bounds.left + bounds.width)
      );

      const bottom = Math.max(
        top + minSize,
        Math.min(drag.startRect.y + drag.startRect.height + dy, bounds.top + bounds.height)
      );

      next.x = left;
      next.y = top;
      next.width = right - left;
      next.height = bottom - top;
    }

    setCropRect(next);
  };

  const endPointerDrag = (e) => {
    const drag = dragRef.current;
    if (!drag.active || drag.pointerId !== e.pointerId) return;

    dragRef.current = {
      active: false,
      mode: null,
      pointerId: null,
      startClientX: 0,
      startClientY: 0,
      startRect: null,
    };

    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (_) {}
  };

  const handleCropButtonClick = async (e) => {
    e.stopPropagation();

    if (uiDisabled || isProcessing || isSubmittingCrop || !localImage) return;

    if (!isCropMode) {
      calculateDisplayedImageRect();
      setIsCropMode(true);
      return;
    }

    if (!cropRect || !displayedImageRect || !naturalSize.width || !naturalSize.height) {
      console.error('Crop confirm aborted: missing crop data');
      return;
    }

    const scaleX = naturalSize.width / displayedImageRect.width;
    const scaleY = naturalSize.height / displayedImageRect.height;

    const payload = {
      imagePath: localImage.split('?')[0],
      x: Math.round((cropRect.x - displayedImageRect.left) * scaleX),
      y: Math.round((cropRect.y - displayedImageRect.top) * scaleY),
      width: Math.round(cropRect.width * scaleX),
      height: Math.round(cropRect.height * scaleY),
    };

    console.log('Crop confirm payload:', payload);

    try {
      setIsSubmittingCrop(true);

      let response;

      if (localImage.startsWith('blob:')) {
        const blobResponse = await fetch(localImage);
        const blob = await blobResponse.blob();

        const formData = new FormData();
        formData.append('x', String(payload.x));
        formData.append('y', String(payload.y));
        formData.append('width', String(payload.width));
        formData.append('height', String(payload.height));
        formData.append('imageFile', blob, 'temp_upload.png');

        response = await fetch('/crop-image/', {
          method: 'POST',
          body: formData,
        });
      } else {
        const formData = new FormData();
        formData.append('x', String(payload.x));
        formData.append('y', String(payload.y));
        formData.append('width', String(payload.width));
        formData.append('height', String(payload.height));
        formData.append('imagePath', payload.imagePath);

        response = await fetch('/crop-image/', {
          method: 'POST',
          body: formData,
        });
      }

      const data = await response.json();
      console.log('Crop response:', data);

      if (!response.ok) {
        throw new Error(
          typeof data.detail === 'string'
            ? data.detail
            : JSON.stringify(data.detail || data)
        );
      }

      const refreshedPath = `${data.croppedImagePath}?t=${Date.now()}`;
      setLocalImage(refreshedPath);

      if (typeof onCropConfirm === 'function') {
        await onCropConfirm({
          ...payload,
          croppedImagePath: data.croppedImagePath,
        });
      }

      if (typeof invalidateProcessing === 'function') {
        invalidateProcessing();
      }

      setIsCropMode(false);
    } catch (err) {
      console.error('Crop confirm failed:', err);
    } finally {
      setIsSubmittingCrop(false);
    }
  };

  const handleBoxClick = clickable && !isCropMode ? onInputClick : undefined;

  const handleDotSize = 26;
  const handleTouchSize = 42;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        marginBottom: '2vh',
      }}
    >
      <div
        ref={boxRef}
        style={{
          ..._boxStyle,
          position: 'relative',
          cursor: clickable && !isCropMode ? 'pointer' : 'default',
          opacity: clickable ? 1 : 0.6,
          userSelect: 'none',
          touchAction: isCropMode ? 'none' : 'auto',
        }}
        onClick={handleBoxClick}
      >
        {localImage && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              zIndex: 30,
              background:
                'linear-gradient(to bottom, rgba(15,23,42,0.96), rgba(15,23,42,0.6), transparent)',
              color: 'white',
              transform: isOpen
                ? 'translateY(0%)'
                : 'translateY(calc(-100% + 2.8vh))',
              transition: 'transform 0.25s ease-in-out',
              willChange: 'transform',
              fontSize: '0.8rem',
              pointerEvents: uiDisabled ? 'none' : 'auto',
              opacity: uiDisabled ? 0.6 : 1,
            }}
          >
            <div
              onClick={
                uiDisabled
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      setIsOpen((prev) => {
                        const next = !prev;
                        if (next) capitanApi?.onOpenBasic?.();
                        return next;
                      });
                    }
              }
              style={{
                height: '2.8vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: uiDisabled ? 'not-allowed' : 'pointer',
                borderBottom: '1px solid rgba(255,255,255,0.4)',
                backgroundColor: 'rgba(112,112,112,0.4)',
              }}
            >
              <span
                style={{
                  fontSize: '2.5vh',
                  fontWeight: 600,
                  letterSpacing: '0.03em',
                }}
              >
                {isOpen ? '▲ Basic Controls ▲' : '▼ Basic Controls ▼'}
              </span>
            </div>

            <div
              style={{
                padding: isOpen ? '8px 10px 10px' : '0 10px 0',
                maxHeight: isOpen ? '55%' : 0,
                overflowY: 'auto',
                overflow: 'hidden',
                backgroundColor: 'rgba(112,112,112,0.5)',
                fontSize: '2vh',
                transition:
                  'max-height 0.25s ease, opacity 0.25s ease, padding 0.25s ease',
                opacity: isOpen ? 1 : 0,
              }}
            >
              {renderNumColors()}
              {renderNumPicks()}
              {renderShape()}
              {renderFlags()}
            </div>
          </div>
        )}

        {localImage ? (
          <>
            <img
              ref={imgRef}
              src={localImage}
              alt="Input"
              style={_imgStyle}
              onLoad={() => {
                calculateDisplayedImageRect();
              }}
            />

            {isCropMode && cropRect && displayedImageRect && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 12,
                  touchAction: 'none',
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    left: cropRect.x,
                    top: cropRect.y,
                    width: cropRect.width,
                    height: cropRect.height,
                    border: '2px solid #ffffff',
                    boxSizing: 'border-box',
                    background: 'transparent',
                    cursor: 'move',
                    boxShadow: '0 0 0 99999px rgba(0,0,0,0.35)',
                    touchAction: 'none',
                  }}
                  onPointerDown={(e) => beginPointerDrag(e, 'move')}
                  onPointerMove={handlePointerMove}
                  onPointerUp={endPointerDrag}
                  onPointerCancel={endPointerDrag}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: '33.333%',
                      top: 0,
                      width: '1px',
                      height: '100%',
                      background: 'rgba(255,255,255,0.7)',
                      pointerEvents: 'none',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: '66.666%',
                      top: 0,
                      width: '1px',
                      height: '100%',
                      background: 'rgba(255,255,255,0.7)',
                      pointerEvents: 'none',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '33.333%',
                      left: 0,
                      width: '100%',
                      height: '1px',
                      background: 'rgba(255,255,255,0.7)',
                      pointerEvents: 'none',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '66.666%',
                      left: 0,
                      width: '100%',
                      height: '1px',
                      background: 'rgba(255,255,255,0.7)',
                      pointerEvents: 'none',
                    }}
                  />

                  {[
                    { key: 'resize-nw', left: -handleTouchSize / 2, top: -handleTouchSize / 2, cursor: 'nwse-resize' },
                    { key: 'resize-ne', right: -handleTouchSize / 2, top: -handleTouchSize / 2, cursor: 'nesw-resize' },
                    { key: 'resize-sw', left: -handleTouchSize / 2, bottom: -handleTouchSize / 2, cursor: 'nesw-resize' },
                    { key: 'resize-se', right: -handleTouchSize / 2, bottom: -handleTouchSize / 2, cursor: 'nwse-resize' },
                  ].map((handle) => (
                    <div
                      key={handle.key}
                      onPointerDown={(e) => beginPointerDrag(e, handle.key)}
                      onPointerMove={handlePointerMove}
                      onPointerUp={endPointerDrag}
                      onPointerCancel={endPointerDrag}
                      style={{
                        position: 'absolute',
                        width: handleTouchSize,
                        height: handleTouchSize,
                        background: 'transparent',
                        cursor: handle.cursor,
                        touchAction: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ...('left' in handle ? { left: handle.left } : {}),
                        ...('right' in handle ? { right: handle.right } : {}),
                        ...('top' in handle ? { top: handle.top } : {}),
                        ...('bottom' in handle ? { bottom: handle.bottom } : {}),
                      }}
                    >
                      <div
                        style={{
                          width: handleDotSize,
                          height: handleDotSize,
                          borderRadius: '50%',
                          background: '#ffffff',
                          border: '1px solid #334155',
                          boxSizing: 'border-box',
                          pointerEvents: 'none',
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={handleCropButtonClick}
              disabled={uiDisabled || isProcessing || isSubmittingCrop || !localImage}
              title={isCropMode ? 'Confirm crop' : 'Start crop'}
              style={{
                position: 'absolute',
                left: '1vh',
                bottom: '1vh',
                zIndex: 20,
                width: '14vmin',
                height: '14vmin',
                borderRadius: '0.8vh',
                border: '1px solid rgba(255,255,255,0.5)',
                background: isCropMode ? 'rgba(34,197,94,0.92)' : 'rgba(15,23,42,0.78)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12vmin',
                cursor: uiDisabled || isProcessing || isSubmittingCrop ? 'not-allowed' : 'pointer',
                backdropFilter: 'blur(4px)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              }}
            >
              {isSubmittingCrop ? '…' : isCropMode ? '✓' : '✂'}
            </button>

            {isProcessing && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 40,
                  pointerEvents: 'none',
                }}
              >
                <img
                  src="/rendering.gif"
                  alt="Rendering"
                  style={{ width: 200, height: 200, objectFit: 'contain' }}
                />
              </div>
            )}
          </>
        ) : (
          <span
            style={{
              fontSize: '2.8vh',
              color: '#64748b',
              textAlign: 'center',
              padding: '1vw',
            }}
          >
            Click Here to upload image
          </span>
        )}
      </div>
    </div>
  );
}